import Markdown from '@/components/dataDisplay/Markdown';
import Stamp from '@/components/dataDisplay/Stamp';
import Tooltip from '@/components/dataDisplay/Tooltip';
import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_SOLUTIONS_GROUP_BY,
} from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { Campus } from '@/modules/core/data/types/Campus';

import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { Customer } from '@/modules/core/data/types/Customer';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { StudentSolutionGroup } from '@/modules/core/data/types/StudentSolutionGroup';
import { StudentSolutionsGroupByType } from '@/modules/core/data/types/StudentSolutionsGroupByType';
import { resolveInitialSearchFilter } from '@/modules/solutions/solutionsFilters';
import { filterBySearchText } from '@/modules/solutions/studentSolution';
import { serializeStudentSolutionContext } from '@/modules/solutions/studentSolutionContext';
import {
  computeStudentSolutionGroups,
  filterStudentSolutionGroups,
  getDefaultLabelByGroup,
} from '@/modules/solutions/studentSolutionGroup';
import { resolvePosition } from '@/modules/solutionsSurvey/feedbackSurvey';
import {
  Amplitude,
  LogOnChange,
  LogOnMount,
} from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import Icon from '@/common/components/dataDisplay/Icon';
import { isBrowser } from '@unly/utils';
import { Info, Search } from 'lucide-react';
import classnames from 'classnames';
import camelCase from 'lodash.camelcase';
import get from 'lodash.get';
import map from 'lodash.map';
import orderBy from 'lodash.orderby';
import size from 'lodash.size';
import {
  NextRouter,
  useRouter,
} from 'next/router';
import React, {
  ChangeEvent,
  Fragment,
  useState,
} from 'react';
import { DebounceInput } from 'react-debounce-input';
import {
  Trans,
  useTranslation,
} from 'react-i18next';
import {
  Col,
  Input,
  Row,
} from 'reactstrap';
import FeedbackSurvey from '../../solutionsSurvey/components/FeedbackSurvey';
import ShareButton from './ShareButton';
import SolutionCard from './SolutionCard';
import SolutionReportDialog from './SolutionReportDialog';

type Props = {
  contextFiltersSettings: ContextFiltersSettings;
  customer: Customer;
  studentSolutionsFilteredByContext: StudentSolution[];
  allCampuses: Campus[];
  defaultGroupBy: StudentSolutionsGroupByType;
  context: StudentSolutionContext;
}

/**
 * Used to count the number of simulations, set to 0 but min value will be 1, because always incremented before being first used.
 * Used for Analytics.
 */
let simulationsCount = 0;

const OurSolutions: React.FunctionComponent<Props> = (props): JSX.Element => {
  // eslint-disable-next-line react/prop-types
  const {
    contextFiltersSettings,
    customer,
    studentSolutionsFilteredByContext,
    allCampuses,
    defaultGroupBy,
    context,
  }: Props = props;
  const { modTFP } = customer;
  const { solutionsIntroduction } = modTFP || {};
  const router: NextRouter = useRouter();
  const { t } = useTranslation();
  const { primaryColor, secondaryColorVariant1, secondaryColor, onSecondaryColor } = useTheme();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState<boolean>(false);
  const [isReportSent, setIsReportSent] = useState<boolean>(false);
  const [selectedStudentSolutionToReport, setSelectedStudentSolutionToReport] = useState<StudentSolution>(null);
  const [groupedBy, setGroupedBy] = useState<StudentSolutionsGroupByType>(defaultGroupBy);
  const [searchFilter, setSearchFilter] = useState<string>(resolveInitialSearchFilter(router));
  const isSearchFilterUsed = !!size(searchFilter);
  const studentSolutionsFilteredBySearchFilter: StudentSolution[] = filterBySearchText(studentSolutionsFilteredByContext, searchFilter); // Search filter is applied at last, those that pass it will be displayed
  const studentSolutionGroups: StudentSolutionGroup[] = computeStudentSolutionGroups(studentSolutionsFilteredBySearchFilter, context, groupedBy, allCampuses, t(`solutionsPage.solutionsGroup.${camelCase(groupedBy)}.defaultLabel`, getDefaultLabelByGroup(groupedBy)));
  const filteredStudentSolutionGroups: StudentSolutionGroup[] = filterStudentSolutionGroups(studentSolutionGroups, context, groupedBy);
  const filteredStudentSolutionsCount: number = size(studentSolutionsFilteredBySearchFilter);
  const feedbackPosition = resolvePosition(filteredStudentSolutionsCount);
  let currentSolutionIndex = 0; // Tracker of the current solution being displayed while looping, used to display the FeedbackSurvey component at the right position

  ++simulationsCount;

  const amplitudeSolutionDisplayedEventProps = {
    simulation: {
      filters: serializeStudentSolutionContext(context),
      filtersMeta: {
        educationalProgramsCount: size(context.educationalPrograms),
        educationalProgramStepsCount: size(context.educationalProgramSteps),
        tagsCount: size(context.categories), // DA tagsCount kept from v3 to avoid breaking existing stats - Might be removed in the future when we won't care about old stats
        categoriesCount: size(context.categories),
        campusesCount: size(context.campuses),
      },
      groupedBy: groupedBy,
      searchFilter: searchFilter || null,
      counter: simulationsCount,
    },
    results: {
      solutions: {
        count: filteredStudentSolutionsCount,
        ref: map(studentSolutionsFilteredBySearchFilter, 'ref'),
      },
    },
  };

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <div
          css={css`
            margin-top: 30px;
            margin-bottom: 30px;

            .btn-selected {
              margin-right: 10px;
              background-color: ${secondaryColor};
              color: ${onSecondaryColor};
              border-radius: 30px;
              border: none;
              padding: 8px 13px 5px 12px;
            }

            .btn-unselected {
              margin-right: 10px;
              background-color: ${secondaryColorVariant1};
              color: ${secondaryColor};
              border-radius: 30px;
              border: none;
              padding: 8px 13px 5px 12px;
            }

            .actions-menu {
              position: relative;

              .search-action-container {
                .search-icon {
                  position: absolute;
                  top: 8px;
                  right: 5px;
                }
              }

              .share-action-container {
                margin-top: 10px;
                float: right;
              }
            }

            .left-input {
              justify-content: space-evenly;
            }

            .mobile-solution-groups-separator {
              display: none;
              margin-right: 10px;
              margin-left: 10px;

              @media (max-width: 992px) {
                display: block;
              }
            }

            .mobile-solution-group-separator {
              display: none;
              border: lightgray 0.5px solid;
              width: 50%;
              max-width: 200px;

              @media (max-width: 991.98px) {
                display: block;
              }
            }

            .center-grid {
              margin-left: 10px;
              margin-right: 10px;
              padding: 0;

              .title {
                b {
                  color: ${primaryColor};
                }
              }

              .list-group-by {
                padding-right: 20px;

                @media (max-width: 991.98px) {
                  display: block;
                }
              }

              @media (min-width: 992px) {
                margin-left: 50px;
                margin-right: 50px;
                padding: 0;
              }
            }

            .no-solutions {
              text-align: center;
              margin-top: 30px;
            }
          `}
        >
          {/* DA Log event both on initial mount and subsequent renders */}
          <LogOnMount
            eventType={'solutions-displayed'}
            eventProperties={amplitudeSolutionDisplayedEventProps}
          />
          <LogOnChange
            // DA Will not trigger even upon initial mount, but only when "OurSolutions" component re-renders
            value={simulationsCount} // When simulationsCount increases, it means there was a re-render (most likely caused by the use of a filter/groupBy)
            eventType={'solutions-displayed'}
            eventProperties={amplitudeSolutionDisplayedEventProps}
          />

          <Row className={'center-grid'}>
            <Col>
              <h2>
                <span
                  className={'title'}
                  dangerouslySetInnerHTML={{
                    __html: !isSearchFilterUsed ?
                      t('solutionsPage.solutionsHeader.title', 'Nos solutions')
                      : t('solutionsPage.solutionsHeader.filteredTitle', 'Nos solutions filtrées pour <b>&ldquo;{{ searchFilter }}&rdquo;</b>', { searchFilter }),
                  }}
                />
                {' '}({filteredStudentSolutionsCount})
                <sup
                  css={css`
                    svg {
                      margin-left: 5px;
                      margin-right: 0 !important;
                      font-size: 12px;
                    }
                  `}
                >
                  <Tooltip
                    overlay={<span>{t('solutionsPage.solutionsHeader.titleCountHelp', 'Nombre de solutions distinctes')}</span>}
                  >
                    <Icon icon={Info} variant="light" />
                  </Tooltip>
                </sup>
              </h2>

              {
                // Use institution content first, then fallback to default content (Locize)
                solutionsIntroduction ? (
                  <Markdown text={solutionsIntroduction} />
                ) : (
                  <div>
                    <p>
                      <Trans
                        i18nKey={'solutionsPage.solutionsHeader.introduction.text'}
                      >
                        Voici les solutions disponibles pour vous.
                      </Trans>
                    </p>
                  </div>
                )
              }
            </Col>
            <Col
              xs={6}
              lg={2}
              className={'left-input'}
            >
              <div className={'actions-menu'}>
                <div className={'search-action-container'}>
                  <DebounceInput
                    // @ts-ignore
                    element={Input}
                    bsSize={'md'}
                    placeholder={t('solutionsPage.solutionsHeader.searchFilter.placeholder', 'Rechercher')}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      setSearchFilter(event.target.value);
                    }}
                    value={searchFilter}
                    debounceTimeout={300}
                  />
                  <span className={'pcolor search-icon'}>
                    <Icon icon={Search} />
                  </span>
                </div>
                <div className={'share-action-container'}>
                  <ShareButton
                    link={isBrowser() ? location.href : null}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {
            // Display filter group only if there are more than one filter enabled
            contextFiltersSettings.enabledFiltersCount > 1 && (
              <Row className={'center-grid'}>
                <Col>
                  <p>
                    <span className={'list-group-by'}>
                      {t('solutionsPage.solutionsHeader.groupBy.label', 'Regrouper les résultats par :')}
                    </span>

                    {
                      contextFiltersSettings.campusFilterEnabled && (
                        <button
                          className={classnames('inline', groupedBy === 'Campus' ? 'btn-selected' : 'btn-unselected')}
                          onClick={(): void => {
                            setGroupedBy('Campus');

                            logEvent('solutions-groupBy-changed', {
                              selection: {
                                oldGroupBy: groupedBy,
                                newGroupBy: AMPLITUDE_SOLUTIONS_GROUP_BY.CAMPUS,
                              },
                              action: AMPLITUDE_ACTIONS.CLICK,
                            });
                          }}
                        >
                          {t('solutionsPage.solutionsHeader.groupByCampus', 'Campus')}
                        </button>
                      )
                    }

                    {
                      contextFiltersSettings.educationalProgramFilterEnabled && (
                        <button
                          className={classnames('inline', groupedBy === 'EducationalProgram' ? 'btn-selected' : 'btn-unselected')}
                          onClick={(): void => {
                            setGroupedBy('EducationalProgram');

                            logEvent('solutions-groupBy-changed', {
                              selection: {
                                oldGroupBy: groupedBy,
                                newGroupBy: AMPLITUDE_SOLUTIONS_GROUP_BY.EDUCATIONAL_PROGRAM,
                              },
                              action: AMPLITUDE_ACTIONS.CLICK,
                            });
                          }}
                        >
                          {t('solutionsPage.solutionsHeader.groupByProgram', 'Programme')}
                        </button>
                      )
                    }

                    {
                      contextFiltersSettings.categoryFilterEnabled && (
                        <button
                          className={classnames('inline', groupedBy === 'Category' ? 'btn-selected' : 'btn-unselected')}
                          onClick={(): void => {
                            setGroupedBy('Category');

                            logEvent('solutions-groupBy-changed', {
                              selection: {
                                oldGroupBy: groupedBy,
                                newGroupBy: AMPLITUDE_SOLUTIONS_GROUP_BY.CATEGORY,
                              },
                              action: AMPLITUDE_ACTIONS.CLICK,
                            });
                          }}
                        >
                          {t('solutionsPage.solutionsHeader.groupByCategory', 'Catégorie')}
                        </button>
                      )
                    }
                  </p>
                </Col>
              </Row>
            )
          }

          <hr className={'mobile-solution-groups-separator'} />

          {/* XXX Display solutions (one by one) */}
          <div>
            {
              // Order groups by display order (not always available), then by label which is the default ordering (displayPriorityScore that are equal to 0 will therefore be ordered by label)
              map(orderBy(filteredStudentSolutionGroups, ['displayPriorityScore', 'label'], ['desc', 'asc']), (group: StudentSolutionGroup, i: number) => {
                // Order solutions by display order (may not always be defined, default to lowest priority), then by label
                const solutions = orderBy(get(group, 'solutions', []), [
                  'displayPriorityScore',
                  'label',
                ], ['desc', 'asc']);

                if (size(solutions) > 0) {
                  return (
                    <Fragment key={group.id}>
                      {
                        i !== 0 && (
                          // Display separator between each group of solutions on mobile
                          <hr className={'mobile-solution-group-separator'} />
                        )
                      }
                      <h3
                        css={css`
                          margin-top: 30px;

                          @media (min-width: 992px) {
                            display: flex !important; // Overrides bootstrap to force using flex with desktop to center vertically elements
                            margin-left: 75px;
                            margin-right: 75px;
                          }

                          @media (max-width: 991.98px) {
                            display: flex;
                            margin-left: 20px;
                            justify-content: space-between;
                          }

                          .solution-subtitle {
                            padding-top: 10px;
                            margin-right: 15px;
                            display: inline;
                          }
                        `}
                      >
                        <div className={'solution-subtitle'}>
                          {t('solutionsPage.solutionsHeader.subtitle', 'Solutions')}
                          {' '}({size(solutions)})
                        </div>
                        {' '}
                        <Stamp>{group.label}</Stamp>
                      </h3>

                      {
                        map(solutions, (studentSolution: StudentSolution, index: number) => {
                          currentSolutionIndex++;

                          return (
                            <Fragment
                              key={studentSolution.id}
                            >
                              <SolutionCard
                                contextFiltersSettings={contextFiltersSettings}
                                studentSolution={studentSolution}
                                index={index}
                                context={context}
                                isReportDialogOpen={isReportDialogOpen}
                                setIsReportDialogOpen={setIsReportDialogOpen}
                                setSelectedStudentSolutionToReport={setSelectedStudentSolutionToReport}
                              />

                              {
                                currentSolutionIndex === feedbackPosition && (
                                  <FeedbackSurvey
                                    solutions={solutions}
                                    context={context}
                                    simulationsCount={simulationsCount}
                                    searchFilter={searchFilter}
                                  />
                                )
                              }
                            </Fragment>
                          );
                        })
                      }
                    </Fragment>
                  );
                }
              })
            }
          </div>

          {
            // Handle case where there are no solutions to display
            filteredStudentSolutionsCount === 0 && (
              <div
                className={'no-solutions'}
              >
                <Trans
                  i18nKey={'solutionsPage.noSolutionsFound.text'}
                >
                  Aucune solution ne correspond à votre recherche. Veuillez revoir les critères sélectionnés.
                </Trans>
              </div>
            )
          }

          {/*
            Always display the report dialog, it will show/hide itself based on its props.
            The SolutionReportDialog can be open/closed from any SolutionCard component.
           */}
          <SolutionReportDialog
            studentSolution={selectedStudentSolutionToReport}
            isReportDialogOpen={isReportDialogOpen}
            setIsReportDialogOpen={setIsReportDialogOpen}
            isReportSent={isReportSent}
            setIsReportSent={setIsReportSent}
          />
        </div>
      )}
    </Amplitude>
  );
};

export default OurSolutions;
